import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["loading", "signButton"]
  static values = {
    uploadingStatusUrl: String,
    signingStatusUrl: String,
    pollInterval: { type: Number, default: 2000 },
    signingPollInterval: { type: Number, default: 4000 },
    maxRetries: { type: Number, default: 1800 }, // ~2 hours at 4s interval
    waitingForSignatureMessage: { type: String, default: "Waiting for signature..." }
  }

  connect() {
    this.polling = true
    this.signingPolling = false
    this.signingRetryCount = 0
    this.nextMonitorMin = 0
    this.checkUploadingStatus()
  }

  disconnect() {
    this.polling = false
    this.signingPolling = false
    this.stopPolling()
    this.stopSigningPolling()
  }

  async checkUploadingStatus() {
    if (!this.polling || !this.uploadingStatusUrlValue) return

    try {
      const response = await fetch(this.uploadingStatusUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        console.error("Failed to fetch uploading status:", response.status)
        this.scheduleNextPoll()
        return
      }

      const data = await response.json()

      if (data.signing_link) {
        this.showSignButton(data.signing_link)
        this.polling = false
      } else {
        this.updateLoadingMessage(data.message)
        this.scheduleNextPoll()
      }
    } catch (error) {
      console.error("Error checking uploading status:", error)
      this.scheduleNextPoll()
    }
  }

  scheduleNextPoll() {
    if (!this.polling) return
    this.pollTimeout = setTimeout(() => this.checkUploadingStatus(), this.pollIntervalValue)
  }

  stopPolling() {
    if (this.pollTimeout) {
      clearTimeout(this.pollTimeout)
      this.pollTimeout = null
    }
  }

  // Called when user clicks the sign button
  checkSigningStatus(event) {
    // Allow the link to open in new tab (don't prevent default)
    // Start polling for signing status
    this.signingPolling = true
    this.signingRetryCount = 0
    this.nextMonitorMin = 0

    // Show loading state and hide sign button
    this.showSigningWaitingState()

    // Start polling after a short delay to give user time to start signing
    setTimeout(() => this.pollSigningStatus(), this.signingPollIntervalValue)
  }

  showSigningWaitingState() {
    if (this.hasSignButtonTarget) {
      this.signButtonTarget.classList.add("hidden")
    }

    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
      this.updateLoadingMessage(this.waitingForSignatureMessageValue)
    }
  }

  async pollSigningStatus() {
    if (!this.signingPolling || !this.signingStatusUrlValue) return

    this.signingRetryCount++

    try {
      const url = new URL(this.signingStatusUrlValue, window.location.origin)
      url.searchParams.set("next_monitor_min", this.nextMonitorMin)

      const response = await fetch(url.toString(), {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        if (response.status === 404 || response.status === 500) {
          const data = await response.json()
          if (data.redirect_url) {
            window.location.href = data.redirect_url
            return
          }
        }
        console.error("Failed to fetch signing status:", response.status)
        this.scheduleNextSigningPoll()
        return
      }

      const data = await response.json()
      this.nextMonitorMin = data.next_monitor_min || 0

      if (data.status === "completed" || this.signingRetryCount > this.maxRetriesValue) {
        this.completeAndRedirect(data.next_step)
      } else if (data.status === "expired" || data.status === "error") {
        this.completeAndRedirect(data.next_step)
      } else {
        // Still pending, continue polling
        if (data.message) {
          this.updateLoadingMessage(data.message)
        }
        this.scheduleNextSigningPoll()
      }
    } catch (error) {
      console.error("Error checking signing status:", error)
      this.scheduleNextSigningPoll()
    }
  }

  scheduleNextSigningPoll() {
    if (!this.signingPolling) return
    this.signingPollTimeout = setTimeout(() => this.pollSigningStatus(), this.signingPollIntervalValue)
  }

  stopSigningPolling() {
    this.signingPolling = false
    if (this.signingPollTimeout) {
      clearTimeout(this.signingPollTimeout)
      this.signingPollTimeout = null
    }
  }

  completeAndRedirect(nextStep) {
    this.stopSigningPolling()
    // Small delay before redirect (same as legacy handler)
    setTimeout(() => {
      window.location.href = nextStep
    }, 2000)
  }

  showSignButton(signingLink) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }

    if (this.hasSignButtonTarget) {
      const link = this.signButtonTarget.querySelector("a")
      if (link) {
        link.href = signingLink
      }
      this.signButtonTarget.classList.remove("hidden")
    }
  }

  updateLoadingMessage(message) {
    if (this.hasLoadingTarget && message) {
      const messageElement = this.loadingTarget.querySelector("[data-message]")
      if (messageElement) {
        messageElement.textContent = message
      }
    }
  }
}
